<script setup lang="ts">
/** DD : HH : MM in green boxes — time left on a deal. */
const props = withDefaults(defineProps<{ endsAt: string; size?: 'sm' | 'md' | 'lg' }>(), { size: 'md' })

const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  timer = setInterval(() => (now.value = Date.now()), 30_000)
})
onBeforeUnmount(() => clearInterval(timer))

const parts = computed(() => {
  const ms = Math.max(0, new Date(props.endsAt).getTime() - now.value)
  const mins = Math.floor(ms / 60_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    ended: ms === 0,
    days: pad(Math.floor(mins / 1440)),
    hours: pad(Math.floor((mins % 1440) / 60)),
    minutes: pad(mins % 60),
  }
})

const label = computed(() =>
  parts.value.ended
    ? 'Deal ended'
    : `${Number(parts.value.days)} days, ${Number(parts.value.hours)} hours and ${Number(parts.value.minutes)} minutes left`,
)
</script>

<template>
  <span class="countdown" :class="`countdown--${size}`" role="timer" :aria-label="label">
    <template v-if="parts.ended"><span class="countdown__ended">Ended</span></template>
    <template v-else>
      <span class="countdown__box">{{ parts.days }}</span>
      <span class="countdown__sep" aria-hidden="true">:</span>
      <span class="countdown__box">{{ parts.hours }}</span>
      <span class="countdown__sep" aria-hidden="true">:</span>
      <span class="countdown__box">{{ parts.minutes }}</span>
    </template>
  </span>
</template>

<style scoped>
.countdown {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.countdown__box {
  display: inline-block;
  min-width: 2.1em;
  padding: 0.1em 0.3em;
  border-radius: 3px;
  background: var(--success-600);
  color: var(--white);
  text-align: center;
}

.countdown__sep {
  color: var(--gray-900);
}

.countdown__ended {
  padding: 0.1em 0.5em;
  border-radius: 3px;
  background: var(--gray-200);
  color: var(--gray-700);
}

.countdown--sm {
  font-size: 12px;
  line-height: 18px;
}

.countdown--md {
  font-size: 14px;
  line-height: 20px;
}

.countdown--lg {
  font-size: 22px;
  line-height: 30px;
  gap: 6px;
}

.countdown--lg .countdown__box {
  padding: 2px 8px;
}
</style>
